import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { playNotificationSound } from '../utils/notificationSound';
import { toast } from 'sonner';
import { 
  MessageCircle, X, Send, Minimize2, Maximize2, 
  User, Bot, Loader2, CheckCircle, Clock, Volume2, VolumeX
} from 'lucide-react';

/**
 * LiveChatPopup - Floating chat widget for live support
 * Shows when agents are available and user has access
 */
export const LiveChatPopup = ({ position = 'bottom-right' }) => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [agentAvailable, setAgentAvailable] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const messagesEndRef = useRef(null);
  const previousMessageCount = useRef(0);
  const pollInterval = useRef(null);

  // Check if user has chat access (not for customers in CRM mode)
  const hasAccess = isAuthenticated && user?.role !== 'customer';

  // Position classes - staggered to avoid overlap with other chat widgets
  const positionClasses = {
    'bottom-right': 'bottom-24 right-6',
    'bottom-left': 'bottom-24 left-6',
    'top-right': 'top-20 right-6',
    'top-left': 'top-20 left-6'
  };

  // Check agent availability
  const checkAvailability = useCallback(async () => {
    if (!hasAccess) return;
    
    try {
      const res = await liveChatAPI.getAvailableAgents();
      const agents = res.data.agents || [];
      setAvailableAgents(agents);
      setAgentAvailable(agents.length > 0);
      
      // Show popup notification if agent becomes available
      if (agents.length > 0 && !agentAvailable && !session) {
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 5000);
      }
    } catch (err) {
      console.debug('Could not check agent availability:', err);
    }
  }, [hasAccess, agentAvailable, session]);

  // Fetch messages for active session
  const fetchMessages = useCallback(async () => {
    if (!session?.id) return;
    
    try {
      const res = await liveChatAPI.getMessages(session.id);
      const newMessages = res.data.messages || [];
      
      // Play sound for new messages from agent
      if (newMessages.length > previousMessageCount.current && previousMessageCount.current > 0) {
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.sender_type === 'agent' && soundEnabled) {
          playNotificationSound('chat', 0.3);
        }
      }
      
      previousMessageCount.current = newMessages.length;
      setMessages(newMessages);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [session, soundEnabled]);

  // Initial load and polling
  useEffect(() => {
    checkAvailability();
    const availInterval = setInterval(checkAvailability, 30000);
    
    return () => {
      clearInterval(availInterval);
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [checkAvailability]);

  // Poll for messages when session is active
  useEffect(() => {
    if (session?.id && isOpen) {
      fetchMessages();
      pollInterval.current = setInterval(fetchMessages, 3000);
    } else if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }
    
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [session, isOpen, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Start a new chat session
  const startChat = async () => {
    setLoading(true);
    try {
      // Generate a unique visitor ID if not already set
      const visitorId = localStorage.getItem('chat_visitor_id') || `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_visitor_id', visitorId);
      
      const res = await liveChatAPI.createSession({
        visitor_id: visitorId,
        visitor_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'Visitor',
        visitor_email: user?.email || '',
        initial_message: 'Hello, I need assistance.'
      });
      
      setSession(res.data);
      setIsOpen(true);
      setIsMinimized(false);
      toast.success('Chat started! An agent will be with you shortly.');
    } catch (err) {
      console.error('Failed to start chat:', err);
      toast.error('Failed to start chat. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !session?.id) return;
    
    setSending(true);
    try {
      await liveChatAPI.sendMessage(session.id, {
        content: newMessage,
        sender_type: 'visitor'
      });
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Close chat
  const closeChat = async () => {
    if (session?.id) {
      try {
        await liveChatAPI.closeChat(session.id);
      } catch (err) {
        console.error('Failed to close chat:', err);
      }
    }
    setSession(null);
    setMessages([]);
    setIsOpen(false);
    previousMessageCount.current = 0;
  };

  // Don't render if no access
  if (!hasAccess) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <div className={`fixed ${positionClasses[position]} z-50`}>
        {/* Availability Popup */}
        {showPopup && !isOpen && (
          <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border p-4 w-64 animate-bounce-in">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <MessageCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Support Available!</p>
                <p className="text-xs text-muted-foreground">
                  {availableAgents.length} agent{availableAgents.length !== 1 ? 's' : ''} online
                </p>
              </div>
              <button onClick={() => setShowPopup(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <Button 
              size="sm" 
              className="w-full mt-3"
              onClick={() => {
                setShowPopup(false);
                if (!session) startChat();
                else setIsOpen(true);
              }}
            >
              Start Chat
            </Button>
          </div>
        )}

        {/* Main Chat Button */}
        {!isOpen && (
          <Button
            size="lg"
            className={`rounded-full h-14 w-14 shadow-lg ${agentAvailable ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700'}`}
            onClick={() => {
              if (session) {
                setIsOpen(true);
                setIsMinimized(false);
              } else if (agentAvailable) {
                startChat();
              } else {
                toast.info('No agents available right now. Please try again later.');
              }
            }}
            disabled={loading}
            data-testid="live-chat-button"
          >
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <>
                <MessageCircle className="h-6 w-6" />
                {agentAvailable && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />
                )}
                {session && messages.length > 0 && (
                  <Badge className="absolute -top-2 -left-2 h-5 w-5 p-0 flex items-center justify-center bg-red-500">
                    {messages.filter(m => m.sender_type === 'agent' && !m.read).length || ''}
                  </Badge>
                )}
              </>
            )}
          </Button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <Card className={`w-80 shadow-2xl overflow-hidden ${isMinimized ? 'h-14' : 'h-[450px]'} transition-all duration-200`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium text-sm">Live Support</p>
                  {session?.assigned_agent ? (
                    <p className="text-xs text-blue-100">{session.assigned_agent}</p>
                  ) : (
                    <p className="text-xs text-blue-100 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Waiting for agent...
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-1.5 hover:bg-white/20 rounded"
                  title={soundEnabled ? 'Mute' : 'Unmute'}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/20 rounded"
                >
                  {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                </button>
                <button
                  onClick={closeChat}
                  className="p-1.5 hover:bg-white/20 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            {!isMinimized && (
              <>
                <ScrollArea className="h-[330px] p-3">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-200" />
                      <p className="text-sm">Start a conversation!</p>
                      <p className="text-xs">An agent will respond shortly.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`flex ${msg.sender_type === 'visitor' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg ${
                              msg.sender_type === 'visitor'
                                ? 'bg-blue-600 text-white rounded-br-sm'
                                : msg.sender_type === 'system'
                                ? 'bg-slate-100 text-slate-600 text-center w-full text-xs'
                                : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                            }`}
                          >
                            {msg.sender_type === 'agent' && (
                              <p className="text-xs font-medium text-blue-600 mb-1">{msg.sender_name || 'Agent'}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${msg.sender_type === 'visitor' ? 'text-blue-200' : 'text-slate-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Input */}
                <form onSubmit={sendMessage} className="p-3 border-t flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    disabled={sending}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button type="submit" size="icon" disabled={sending || !newMessage.trim()}>
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              </>
            )}
          </Card>
        )}
      </div>

      {/* CSS for bounce animation */}
      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0.8) translateY(10px); opacity: 0; }
          50% { transform: scale(1.05) translateY(-5px); }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        .animate-bounce-in {
          animation: bounce-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default LiveChatPopup;
